"use client";

import { Button, Checkbox, Modal } from "components/ui/v2";
import { cn } from "@/lib/utils";
import { Check, MessageCircle } from "lucide-react";

export interface InteractiveGuideDialogV2Props {
  open: boolean;
  title: string;
  subtitle: string;
  lines: Array<string | JSX.Element>;
  closeLabel: string;
  dontShowAgainLabel: string;
  dontShowAgainChecked: boolean;
  onDontShowAgainChange: (checked: boolean) => void;
  onClose: () => void;
  animation?: React.ReactNode;
  animationWrapperClassName?: string;
  mobileAccentClassName?: string;
}

export function InteractiveGuideDialogV2({
  open,
  title,
  subtitle,
  lines,
  closeLabel,
  dontShowAgainLabel,
  dontShowAgainChecked,
  onDontShowAgainChange,
  onClose,
  animation,
  animationWrapperClassName = "bg-indigo-50/80 dark:bg-indigo-500/10",
  mobileAccentClassName = "text-indigo-600 dark:text-indigo-300",
}: InteractiveGuideDialogV2Props) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={title}
      description={subtitle}
      closeButtonLabel={closeLabel}
      backdrop
      keyboard
      panelClassName="max-w-[min(100vw-2.5rem,84rem)]"
      bodyClassName="bg-transparent p-0 dark:bg-transparent"
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Checkbox
            checked={dontShowAgainChecked}
            onCheckedChange={(checked) => onDontShowAgainChange(Boolean(checked))}
            label={dontShowAgainLabel}
          />

          <Button
            variant="primary"
            size="md"
            shape="rounded"
            onClick={onClose}
            rightIcon={<Check className="h-4 w-4" />}
          >
            {closeLabel}
          </Button>
        </div>
      }
    >
      <div className="relative">
        <div className="grid gap-4 px-4 pb-4 pt-4 md:grid-cols-[88px_1fr] md:px-5">
          {animation ? (
            <div className="hidden md:flex md:items-start md:justify-center">
              <div
                className={cn(
                  "flex h-20 w-20 items-center justify-center rounded-full shadow-inner",
                  animationWrapperClassName
                )}
              >
                {animation}
              </div>
            </div>
          ) : null}

          <div className="grid gap-2.5 text-sm leading-6 text-slate-600 dark:text-zinc-300 md:text-[13px] md:leading-6">
            <div
              className={cn(
                "mb-1 inline-flex items-center gap-2 md:hidden",
                mobileAccentClassName
              )}
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-xs font-semibold">{subtitle}</span>
            </div>
            {lines.map((line, index) => (
              <div key={index}>{typeof line === "string" ? <p>{line}</p> : line}</div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
